import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { catalogApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Download, FileText, KeyRound, PlayCircle, ArrowLeft, UploadCloud } from 'lucide-react';
import {
  PRODUCT_STATUS_LABEL, PRODUCT_TYPE_LABEL,
  t,
} from '../lib/catalogI18n';

export default function CatalogProduct() {
  const { productId } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [prod, setProd] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [indicatorCode, setIndicatorCode] = useState('PROD_ALU_T');

  useEffect(() => {
    catalogApi.get(productId).then(r => setProd(r.data))
      .catch(() => { toast.error('Producto no encontrado'); nav('/catalogo'); });
  }, [productId, nav]);

  const downloadDcat = async () => {
    const r = await catalogApi.dcat(productId);
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/ld+json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${prod.code}.dcat.jsonld`; a.click();
    URL.revokeObjectURL(url);
  };

  const doRequestAccess = async () => {
    if (!accepted) return toast.error('Debe aceptar las condiciones');
    setRunning(true);
    try {
      const r = await catalogApi.requestAccess(productId, {
        terms_version: prod.terms_version, terms_accepted: true, indicator_code: indicatorCode,
      });
      toast.success(`Solicitud aceptada: ${r.data.request_code}`);
      const tx = await catalogApi.accessTransaction(r.data.id, { indicator_code: indicatorCode });
      setResult(tx.data);
      toast.success(`Transacción ${tx.data.transaction_code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al solicitar acceso');
    } finally { setRunning(false); }
  };

  if (!prod) return <div className="p-6 text-gray-500">Cargando…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="catalog-product-page">
      <Link to="/catalogo" className="text-sm text-slate-600 hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Volver al catálogo
      </Link>

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-mono text-gray-500" data-testid="product-code">{prod.code}</p>
            <h1 className="text-2xl font-bold text-gray-900">{prod.title}</h1>
          </div>
          <Badge className="bg-emerald-100 text-emerald-800" data-testid="product-status-badge">
            {t(PRODUCT_STATUS_LABEL, prod.status)}
          </Badge>
        </div>
        <p className="text-gray-700">{prod.description}</p>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-4">
          <div><dt className="text-gray-500">Tipo</dt><dd className="font-medium">{t(PRODUCT_TYPE_LABEL, prod.product_type)}</dd></div>
          <div><dt className="text-gray-500">Sector</dt><dd className="font-medium">{prod.sector}</dd></div>
          <div><dt className="text-gray-500">Responsable</dt><dd className="font-medium">{prod.publisher_type}</dd></div>
          <div><dt className="text-gray-500">Operador</dt><dd className="font-medium">{prod.operator}</dd></div>
          <div><dt className="text-gray-500">Formatos</dt><dd className="font-medium">{(prod.formats || []).join(', ')}</dd></div>
          <div><dt className="text-gray-500">Periodicidad</dt><dd className="font-medium">{prod.update_frequency}</dd></div>
          <div><dt className="text-gray-500">Acceso</dt><dd className="font-medium">{prod.access_rights}</dd></div>
          <div><dt className="text-gray-500">Umbral agregación</dt><dd className="font-medium">≥ {prod.aggregation_threshold}</dd></div>
          <div><dt className="text-gray-500">Versión metadatos</dt><dd className="font-medium">{prod.metadata_version}</dd></div>
          <div><dt className="text-gray-500">Versión condiciones</dt><dd className="font-medium">{prod.terms_version}</dd></div>
        </dl>

        <div className="pt-3 flex items-center gap-2">
          <Button variant="outline" onClick={downloadDcat} data-testid="dcat-download">
            <Download className="w-4 h-4 mr-2" /> Descargar metadatos JSON-LD
          </Button>
          {isAdmin && prod.status === 'published' && (
            <Button variant="outline" onClick={() => catalogApi.withdraw(productId).then(() => nav('/catalogo'))}>
              Retirar
            </Button>
          )}
        </div>
      </div>

      {/* Sección condiciones + acción según tipo */}
      {prod.product_type === 'service' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Solicitar acceso al servicio
          </h2>
          <div className="text-sm text-gray-600 whitespace-pre-line bg-gray-50 rounded p-3 border">
            <p className="font-medium mb-1">Condiciones · v{prod.terms_version}</p>
            {prod.terms_text}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm">Indicador:
              <select value={indicatorCode} onChange={e => setIndicatorCode(e.target.value)}
                className="ml-2 border rounded px-2 py-1" data-testid="indicator-select">
                <option value="PROD_ALU_T">PROD_ALU_T</option>
                <option value="ENERGIA_KWH">ENERGIA_KWH</option>
                <option value="EXPORT_EUR">EXPORT_EUR</option>
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="terms-checkbox" />
            He leído y acepto las condiciones aplicables a este producto de datos.
          </label>
          <Button onClick={doRequestAccess} disabled={!accepted || running}
                  data-testid="request-access-btn">
            <PlayCircle className="w-4 h-4 mr-2" />
            {running ? 'Procesando…' : 'Solicitar acceso y consultar'}
          </Button>

          {result && (
            <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1"
                 data-testid="access-result">
              <p><strong>Transacción:</strong> {result.transaction_code}</p>
              <p><strong>Tamaño muestra (n):</strong> {result.aggregation.n}</p>
              <p><strong>Media:</strong> {Number(result.aggregation.mean).toFixed(2)} · <strong>Mediana:</strong> {Number(result.aggregation.median).toFixed(2)}</p>
              <p><strong>P25 / P75:</strong> {Number(result.aggregation.p25).toFixed(2)} / {Number(result.aggregation.p75).toFixed(2)}</p>
              <p><strong>Mi valor:</strong> {result.own_value ?? '—'} ({result.own_period || '—'})</p>
              <p className="text-xs text-gray-500">Sin datos individuales de terceros.</p>
            </div>
          )}
        </div>
      )}

      {prod.product_type === 'contribution' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" /> Publicar aportación
          </h2>
          <div className="text-sm text-gray-600 whitespace-pre-line bg-gray-50 rounded p-3 border">
            <p className="font-medium mb-1">Condiciones · v{prod.terms_version}</p>
            {prod.terms_text}
          </div>
          <PublishContributionForm productId={productId} prod={prod} onDone={() => nav('/mis-publicaciones')} />
        </div>
      )}
    </div>
  );
}

function formatDateEs(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch { return iso; }
}

function PublishContributionForm({ productId, prod, onDone }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    catalogApi.myPublishableSubmissions()
      .then(r => {
        setItems(r.data || []);
        // Autoselección de la más reciente si sólo hay una
        if ((r.data || []).length > 0 && !selectedId) {
          setSelectedId(r.data[0].submission_id);
        }
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = items.find(i => i.submission_id === selectedId);

  const submit = async () => {
    if (!selected) return toast.error('Debe seleccionar una carga validada');
    if (!accepted) return toast.error('Debe aceptar las condiciones');
    setBusy(true);
    try {
      const r = await catalogApi.createPublication(productId, {
        submission_id: selected.submission_id,
        campaign_id: selected.campaign_id,
        terms_version: prod.terms_version,
        terms_accepted: true,
      });
      toast.success(`Publicación creada: ${r.data.publication_code}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al publicar');
      // Si la carga fue publicada por otro proceso concurrente, refrescamos el selector
      if (e.response?.status === 409 || e.response?.status === 404) load();
    } finally { setBusy(false); }
  };

  if (loading) {
    return <p className="text-sm text-gray-500" data-testid="pub-loading">Cargando cargas publicables…</p>;
  }

  if (items.length === 0) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm space-y-3"
           data-testid="pub-empty-state">
        <p className="text-amber-900 font-medium">
          No dispone de ninguna aportación validada pendiente de publicación.
        </p>
        <p className="text-amber-800">
          Suba primero sus datos para una campaña abierta.
        </p>
        <Button variant="outline" onClick={() => nav('/subir-datos')} data-testid="go-to-upload-btn">
          <UploadCloud className="w-4 h-4 mr-2" />
          Ir a Subir Datos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-2" data-testid="pub-form">
      <div>
        <label className="text-sm text-gray-700 block mb-1">
          Seleccione la carga validada que desea publicar
        </label>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          className="border rounded px-3 py-2 w-full text-sm bg-white"
          data-testid="pub-submission-select"
        >
          {items.map(it => (
            <option key={it.submission_id} value={it.submission_id}>
              {(it.campaign_name || it.period_label || 'Campaña')} — carga validada el {formatDateEs(it.validated_at || it.submitted_at)} — {it.valid_rows} filas válidas
            </option>
          ))}
        </select>
        {selected && (
          <p className="text-xs text-gray-500 mt-1" data-testid="pub-selected-summary">
            Campaña: <strong>{selected.campaign_name || selected.period_label}</strong> ·
            {' '}Filas válidas: <strong>{selected.valid_rows}</strong> ·
            {' '}Errores: <strong>{selected.error_rows}</strong>
          </p>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="pub-terms-checkbox" />
        He leído y acepto las condiciones aplicables a este producto de datos.
      </label>
      <Button onClick={submit}
              disabled={busy || !selected || !accepted}
              data-testid="publish-contribution-btn">
        {busy ? 'Publicando…' : 'Publicar aportación'}
      </Button>
    </div>
  );
}
